import { useEffect, useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import { useCapa } from '../ui/navegacionReact';
import { CAMPOS, avisoDePropuestas, nombreDeFuente } from './ficha';

/* =====================================================================
   LO DADO DE ALTA SIN INTERNET · DIRECTRIZ-ALTA-POR-ESCANEO F8
   =====================================================================
   Cuando vuelve la conexión, el servidor busca los datos de lo que se dio
   de alta a mano y los deja como PROPUESTA. Aquí se ven y se deciden:
   nada cambia hasta que alguien toca «Aplicar lo marcado».

   Sin propuestas no se pinta nada (no hay estado vacío que mostrar: la
   pantalla de alta sigue siendo la de siempre).
   ===================================================================== */
const ETIQUETA = Object.fromEntries(CAMPOS.map((c) => [c.campo, c.etiqueta]));

function mostrar(v) {
  return Array.isArray(v) ? v.join(', ') : String(v ?? '');
}

export default function Propuestas() {
  const [lista, setLista] = useState([]);
  const [abierto, setAbierto] = useState(false);
  // Abierta, la lista ocupa una entrada del historial: atrás la cierra.
  useCapa(abierto, () => setAbierto(false));

  useEffect(() => {
    api.get('/alta-escaneo/propuestas').then((r) => setLista(Array.isArray(r) ? r : [])).catch(() => setLista([]));
  }, []);

  const aviso = avisoDePropuestas(lista);
  if (!aviso) return null;
  return (
    <div className="rounded-xl border border-[var(--color-marca-borde)] bg-[var(--color-marca-suave)] p-3 space-y-2"
      data-alta-escaneo="propuestas">
      <button type="button" onClick={() => setAbierto((a) => !a)}
        className="w-full min-h-11 flex items-center justify-between gap-2 text-left text-sm font-semibold text-[var(--color-marca-oscura)]">
        <span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4" aria-hidden="true" />{aviso}</span>
        {abierto ? <ChevronUp className="h-4 w-4" aria-hidden="true" /> : <ChevronDown className="h-4 w-4" aria-hidden="true" />}
      </button>
      {abierto && (
        <div className="space-y-3">
          <Ayuda id="alta_escaneo.propuestas" />
          {lista.map((p) => (
            <Propuesta key={p.id} p={p} onHecha={() => setLista((l) => l.filter((x) => x.id !== p.id))} />
          ))}
        </div>
      )}
    </div>
  );
}

function Propuesta({ p, onHecha }) {
  const campos = Object.entries(p.propuesta?.campos || {}).filter(([k]) => ETIQUETA[k]);
  // Por omisión se marca todo MENOS el nombre: el nombre lo escribió alguien
  // de la empresa, y cambiarlo es una decisión aparte.
  const [marcados, setMarcados] = useState(() => new Set(campos.map(([k]) => k).filter((k) => k !== 'nombre')));
  const [imagen, setImagen] = useState(Boolean(p.id_imagen_propuesta));
  const [error, setError] = useState(null);
  const [hecho, setHecho] = useState(null);

  async function decidir(aceptar) {
    setError(null);
    try {
      if (aceptar) {
        await api.post(`/alta-escaneo/propuestas/${p.id}/aceptar`, { campos: [...marcados], imagen });
        setHecho(`Listo: se aplicó a ${p.sku}.`);
      } else {
        await api.post(`/alta-escaneo/propuestas/${p.id}/descartar`, {});
        setHecho(`Descartada: ${p.sku} se queda como estaba.`);
      }
      setTimeout(onHecha, 1200);
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No hay conexión con el servidor. Vuelve a intentarlo.');
    }
  }

  if (hecho) {
    return <p className="text-sm text-green-800 inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4" aria-hidden="true" />{hecho}</p>;
  }
  return (
    <div className="rounded-lg bg-white border border-gray-200 p-3 space-y-2" data-propuesta={p.id}>
      <p className="text-sm font-semibold text-gray-900 break-words">{p.nombre} <span className="font-mono text-xs text-gray-500">{p.sku}</span></p>
      {campos.map(([k, v]) => (
        <label key={k} className="flex items-start gap-2 text-sm min-h-8">
          <input type="checkbox" className="mt-1 h-4 w-4" checked={marcados.has(k)}
            onChange={(e) => setMarcados((s) => { const n = new Set(s); if (e.target.checked) n.add(k); else n.delete(k); return n; })} />
          <span><span className="text-gray-500">{ETIQUETA[k]}:</span> {mostrar(v)}
            {p.propuesta?.origen?.[k] && <span className="text-[11px] text-gray-400"> · de {nombreDeFuente(p.propuesta.origen[k])}</span>}
          </span>
        </label>
      ))}
      {p.id_imagen_propuesta && (
        <label className="flex items-center gap-2 text-sm min-h-8">
          <input type="checkbox" className="h-4 w-4" checked={imagen} onChange={(e) => setImagen(e.target.checked)} />
          Usar la imagen encontrada
        </label>
      )}
      {error && <p role="alert" className="text-xs text-red-700">{error}</p>}
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => decidir(true)} disabled={!marcados.size && !imagen}
          className="min-h-11 bg-[var(--color-marca)] disabled:opacity-60 text-white font-semibold rounded-xl px-3 text-sm">
          Aplicar lo marcado
        </button>
        <button type="button" onClick={() => decidir(false)}
          className="min-h-11 bg-white border border-gray-300 text-gray-800 font-semibold rounded-xl px-3 text-sm">
          Descartar
        </button>
      </div>
      {!marcados.size && !imagen && <p className="text-xs text-gray-600">Marca al menos un dato o la imagen.</p>}
    </div>
  );
}
